import {
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { AlertTriangle, Plus, Search } from "lucide-react"

import type { EmailRecipient } from "../email-target-selector"
import { isValidRecipientEmail } from "../email-target-selector/recipients"

/** True when a recipient row's email_confidence flags it as a guessed (not
 * verified/likely) address — the "guessed email" indicator on a results row. */
function isGuessedEmail(recipient: EmailRecipient): boolean {
  const confidence = recipient.email_confidence
  return confidence === "guessed" || confidence === "unknown"
}

export interface ResultsListProps {
  people: EmailRecipient[]
  total: number
  isLoading: boolean
  /** True when the person's email is currently IN the selection. */
  isSelected: (recipient: EmailRecipient) => boolean
  disabled?: boolean
  /** Current search query — a SERVER-SIDE find across the whole directory, so any
   * contact is reachable (not just the loaded page). Controlled by the shell. */
  search: string
  onSearchChange: (value: string) => void
  /** Toggles a row's membership (see AudienceBuilder#handleRowToggle). */
  onToggle: (recipient: EmailRecipient) => void
  /** Adds a manual (not-in-contacts) recipient typed as a full email address —
   * on Enter or the "Add" chip — for an address that matches nobody. */
  onAddManual?: (recipient: EmailRecipient) => void
  onLoadMore?: () => void
  hasMore?: boolean
}

/**
 * Results area of the Audience Builder: a SERVER-SIDE search box (find any
 * contact by name/email/company across the whole directory, or type a full email
 * + Enter to add someone who isn't a contact), the people list matching the
 * current selection or search, a per-row membership checkbox, and a "guessed
 * email" flag when email_confidence indicates a low-confidence address.
 */
export function ResultsList({
  people,
  total,
  isLoading,
  isSelected,
  disabled = false,
  search,
  onSearchChange,
  onToggle,
  onAddManual,
  onLoadMore,
  hasMore = false,
}: ResultsListProps) {
  const roundedTotal = Math.round(total)

  const trimmedSearch = search.trim()
  const isTypedEmail = isValidRecipientEmail(trimmedSearch)
  const matchesListedPerson =
    isTypedEmail && people.some((p) => p.email.toLowerCase() === trimmedSearch.toLowerCase())
  const addableEmail = isTypedEmail && !matchesListedPerson ? trimmedSearch : null

  const handleAddManual = () => {
    if (!addableEmail || !onAddManual) return
    onAddManual({ id: `manual_${addableEmail}`, name: addableEmail, email: addableEmail, type: "manual" })
    onSearchChange("")
  }

  return (
    <Card variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, minHeight: 320 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {roundedTotal.toLocaleString()} {roundedTotal === 1 ? "person" : "people"}{" "}
        {trimmedSearch ? "found" : "match the current filters"}
      </Typography>

      <TextField
        size="small"
        fullWidth
        placeholder="Search by name, email, or company — or type a full email + Enter to add…"
        value={search}
        disabled={disabled}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && addableEmail) {
            e.preventDefault()
            handleAddManual()
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={14} />
            </InputAdornment>
          ),
        }}
      />

      {addableEmail && onAddManual && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            No contact found — press Enter to add this address.
          </Typography>
          <Chip
            clickable
            size="small"
            variant="outlined"
            icon={<Plus size={14} />}
            disabled={disabled}
            onClick={handleAddManual}
            label={`Add ${addableEmail}`}
          />
        </Box>
      )}

      {isLoading && people.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : people.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
          {trimmedSearch ? "No contacts match your search." : "No people match the current filters."}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ maxHeight: 420, overflowY: "auto" }}>
          {people.map((person) => {
            const checked = isSelected(person)
            const guessed = isGuessedEmail(person)
            return (
              <ListItem key={person.id} divider disablePadding>
                <ListItemIcon sx={{ minWidth: 0 }}>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onToggle(person)}
                    inputProps={{ "aria-label": `${person.name} (${person.email})` }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {person.name}
                      </Typography>
                      {guessed && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          icon={<AlertTriangle size={11} />}
                          label="Guessed email"
                          sx={{ fontSize: 10.5, height: 18, "& .MuiChip-label": { px: 0.5 }, "& .MuiChip-icon": { ml: 0.5 } }}
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {person.email}
                      {person.company ? ` · ${person.company}` : ""}
                    </Typography>
                  }
                />
              </ListItem>
            )
          })}
        </List>
      )}

      {hasMore && !trimmedSearch && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Chip
            clickable
            size="small"
            variant="outlined"
            label="Load more"
            disabled={disabled || isLoading}
            onClick={onLoadMore}
          />
        </Box>
      )}
    </Card>
  )
}

export default ResultsList
